import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  SearchableSelect,
  useToast,
  type SearchableSelectOption,
} from '@kss/ui';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import {
  customerService,
  enumEntries,
  invoiceService,
  invoiceItemService,
  salesOrderService,
  salesOrderItemService,
  productService,
  type Invoice,
  type InvoiceCreate,
  type InvoiceItem,
  type SalesOrderItem,
} from '@kss/api';
import { decodeId } from '../../utils/idCodec';
import {
  loadProductOptions,
  loadStoreOptions,
} from '../shared/formLoaders';
import ImagePicker from '../../components/ImagePicker';
import NumberField from '../../components/NumberField';
import { persistImage } from '../../utils/persistImage';
import { useModulePerms } from '../../hooks/useModulePerms';

/**
 * Invoice form — SSO ISSUE 4 rewrite (2026-07-13).
 *
 * The legacy `CrudForm`-based version couldn't populate line items when
 * an admin picked a Sales Order (Issue 4) and had no field for an
 * authorized signature. This custom form:
 *
 *   - Fetches the picked SO via `salesOrderService.get()` and mirrors
 *     `so.items[]` into the local items table (Description / Qty /
 *     Unit price / Tax rate / Amount all populated). Header totals
 *     recompute from `items` on every change so the backend balance
 *     validator never trips.
 *   - Renders an editable items grid so the admin can tweak lines
 *     inline (or add ad-hoc rows without a SO link).
 *   - Adds an `<ImagePicker />` for the signature at the bottom. On
 *     save the data-URL is uploaded via `persistImage()` and the
 *     returned hosted URL is what actually persists.
 *
 * Enum labels (Status / GstType) are kept human-readable in form
 * state and converted to their int wire values at save time — mirrors
 * the SSO ISSUE 6 pattern used by NewSalesOrder.
 */

/* Single source of truth — see enums.ts InvoiceStatusLabels. */
const STATUS_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('InvoiceStatus').map((e) => [e.label, e.value]),
);
const LABEL_BY_STATUS: Record<number, string> = Object.fromEntries(
  Object.entries(STATUS_BY_LABEL).map(([k, v]) => [v, k]),
);

/* Single source of truth — see enums.ts GstTypeLabels. */
const GST_TYPE_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('GstType').map((e) => [e.label, e.value]),
);
const LABEL_BY_GST_TYPE: Record<number, string> = Object.fromEntries(
  Object.entries(GST_TYPE_BY_LABEL).map(([k, v]) => [v, k]),
);

/**
 * Line-item form row. `productId` is required for the backend, but the
 * admin can still enter free-text `description` (mirrored into
 * `itemName` on save) for non-catalog charges.
 */
interface ItemRow {
  productId: string;
  description: string;
  hsnCode: string;
  quantity: number;
  unitPrice: number;
  /** Combined GST rate — split into CGST/SGST or dropped into IGST at save. */
  taxRate: number;
}

/**
 * SSO ISSUE 8 — one row in the "Suggested items from previous orders"
 * panel. Extends `ItemRow` with light metadata (`orderCount`,
 * `lastOrderedAt`) used only for the suggestion UI, never sent on the
 * wire.
 */
interface SuggestedItem extends ItemRow {
  lastOrderedAt?: string;
  orderCount: number;
}

interface FormState {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  customerId: string;
  salesOrderId: string;
  storeId: string;
  gstType: string;
  status: string;
  notes: string;
  signatureImageUrl: string;
  /**
   * F2 — non-line-item delivery / handling charge. Part of the invoice
   * grand total but never a taxable line, so it is excluded from
   * `Σ items[].total` and carried in the header's `shippingAmount`.
   * Prefilled from the linked Sales Order so a converted SO keeps the
   * delivery fee the customer actually paid.
   */
  shippingAmount: number;
  items: ItemRow[];
}

const today = new Date().toISOString().slice(0, 10);
const emptyItem: ItemRow = {
  productId: '',
  description: '',
  hsnCode: '',
  quantity: 1,
  unitPrice: 0,
  taxRate: 0,
};
const emptyForm: FormState = {
  invoiceNumber: '',
  invoiceDate: today,
  dueDate: today,
  customerId: '',
  salesOrderId: '',
  storeId: '',
  gstType: 'Intra-state (CGST+SGST)',
  status: 'Draft',
  notes: '',
  signatureImageUrl: '',
  shippingAmount: 0,
  items: [],
};

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

/** Line-level totals used by both the header summary and the wire payload. */
function computeLine(row: ItemRow) {
  const taxable = Math.round(row.quantity * row.unitPrice * 100) / 100;
  const tax = Math.round(taxable * (row.taxRate / 100) * 100) / 100;
  const total = Math.round((taxable + tax) * 100) / 100;
  return { taxable, tax, total };
}

/** Round to two decimals — avoids ₹0.001 drift across recomputes. */
const r2 = (n: number) => Math.round(n * 100) / 100;

export default function NewInvoice() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';
  const perms = useModulePerms('Invoice');
  const canSave = isEdit ? perms.canUpdate : perms.canCreate;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /**
   * SSO ISSUE 8 — "Suggested items from previous orders". Rebuilt each
   * time the customer picker changes. Each row is a ready-to-append
   * `ItemRow` sourced from a de-duplicated union of the customer's
   * historical SalesOrder lines (most recent price wins).
   */
  const [suggestedItems, setSuggestedItems] = useState<SuggestedItem[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  /** Cached product options — the row-level pickers all share one loader. */
  const productOptsPromise = useRef<Promise<SearchableSelectOption[]> | null>(null);
  const loadProductOpts = () => {
    if (!productOptsPromise.current) productOptsPromise.current = loadProductOptions();
    return productOptsPromise.current;
  };

  /**
   * SSO ISSUE 7 — Customer dropdown must ONLY list customers that
   * actually transacted (own at least one non-cancelled SalesOrder or
   * one Invoice). Sample rows the storefront checkout stamps with
   * `customerId = user.id` never create a Customer row, so they get
   * filtered out naturally by the Set membership check below. Wrapped
   * in `useCallback` with an empty dep list so `SearchableSelect`'s
   * eager-load `useEffect` fires exactly once per mount.
   */
  const loadCustomerOptsWithHistory = useCallback(async (): Promise<SearchableSelectOption[]> => {
    const [customers, orders, invoices] = await Promise.all([
      customerService.all().catch(() => []),
      salesOrderService.all().catch(() => []),
      invoiceService.all().catch(() => []),
    ]);
    const withHistory = new Set<string>();
    for (const o of orders ?? []) {
      // Status 5 = Cancelled — those don't count as "real" activity.
      const cancelled = typeof o.status === 'number'
        ? o.status === 5
        : String(o.status ?? '').toLowerCase() === 'cancelled';
      if (o.customerId && !cancelled) withHistory.add(o.customerId);
    }
    for (const inv of invoices ?? []) {
      if (inv.customerId) withHistory.add(inv.customerId);
    }
    return (customers ?? [])
      .filter((c) => c.customerId && withHistory.has(c.customerId))
      .map((c) => ({
        value: c.customerId ?? '',
        label: c.name,
        description: c.email ?? c.phone ?? c.code,
        disabled: !c.customerId,
      }));
  }, []);

  /**
   * SSO ISSUE 8 — Sales Order dropdown should surface the product mix
   * so an admin can identify the order visually ("SO-1042 — Widget,
   * Gadget, +2 more") instead of blindly picking an opaque order
   * number. Pre-fetches SO items + product names once and joins them
   * client-side so a tenant with a few hundred SOs doesn't need one
   * `salesOrderService.get()` round-trip per option.
   */
  const loadSalesOrderOptsWithItems = useCallback(async (): Promise<SearchableSelectOption[]> => {
    const [orders, items, products] = await Promise.all([
      salesOrderService.all().catch(() => []),
      salesOrderItemService.all().catch(() => [] as SalesOrderItem[]),
      productService.all().catch(() => []),
    ]);
    const productNameById = new Map<string, string>();
    for (const p of products ?? []) {
      if (p.productId) productNameById.set(p.productId, p.name ?? '');
    }
    const itemsBySoId = new Map<string, SalesOrderItem[]>();
    for (const it of items ?? []) {
      if (!it.salesOrderId) continue;
      const arr = itemsBySoId.get(it.salesOrderId);
      if (arr) arr.push(it);
      else itemsBySoId.set(it.salesOrderId, [it]);
    }
    return (orders ?? []).map((o) => {
      // Prefer inline `items` (some list endpoints denormalise them);
      // fall back to the item-service join for the common case.
      const soItems = (o.items && o.items.length > 0
        ? o.items
        : itemsBySoId.get(o.salesOrderId ?? '')) ?? [];
      const names = soItems
        .map((it) => productNameById.get(it.productId ?? '') || '')
        .filter((n): n is string => !!n);
      const head = names.slice(0, 2).join(', ');
      const rest = names.length > 2 ? `, +${names.length - 2} more` : '';
      const suffix = head ? ` — ${head}${rest}` : '';
      return {
        value: o.salesOrderId ?? '',
        label: `${o.orderNumber || o.salesOrderId || '—'}${suffix}`,
        description: o.status != null ? String(o.status) : undefined,
        disabled: !o.salesOrderId,
      };
    });
  }, []);

  /* Load the existing invoice + its items when editing. */
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true);
        setErr(null);
        try {
          const [rec, items] = await Promise.all([
            invoiceService.get(id),
            invoiceItemService.filter({ invoiceId: id }).catch(() => [] as InvoiceItem[]),
          ]);
          if (cancelled) return;
          setForm({
            invoiceNumber: rec.invoiceNumber ?? '',
            invoiceDate: rec.invoiceDate?.slice(0, 10) ?? today,
            dueDate: rec.dueDate?.slice(0, 10) ?? today,
            customerId: rec.customerId ?? '',
            salesOrderId: rec.salesOrderId ?? '',
            storeId: rec.storeId ?? '',
            gstType:
              typeof rec.gstType === 'number'
                ? LABEL_BY_GST_TYPE[rec.gstType] ?? 'Intra-state (CGST+SGST)'
                : String(rec.gstType ?? 'Intra-state (CGST+SGST)'),
            status:
              typeof rec.status === 'number'
                ? LABEL_BY_STATUS[rec.status] ?? 'Draft'
                : String(rec.status ?? 'Draft'),
            notes: rec.notes ?? '',
            signatureImageUrl: rec.signatureImageUrl ?? '',
            shippingAmount: Number(rec.shippingAmount ?? 0) || 0,
            items: (items ?? []).map((it) => ({
              productId: it.productId ?? '',
              description: it.itemName ?? '',
              hsnCode: it.hsnCode ?? '',
              quantity: Number(it.quantity ?? 0),
              unitPrice: Number(it.unitPrice ?? 0),
              taxRate: Number(
                (it.cgstRate ?? 0) + (it.sgstRate ?? 0) + (it.igstRate ?? 0),
              ),
            })),
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setErr(msg);
          toastRef.current.error('Load failed', msg);
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else {
        setForm(emptyForm);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  /**
   * SSO ISSUE 8 — when the admin picks a customer, look up every
   * SalesOrder they've placed and offer their historical products as a
   * one-click "add row" list. Dedup by productId (most recent price
   * wins) so a customer who ordered "Widget" ten times still shows one
   * "Widget" suggestion. Silent on empty history — first-time
   * customers just don't see the block.
   */
  const handleCustomerChange = async (cid: string) => {
    set('customerId', cid);
    setSuggestedItems([]);
    if (!cid) return;
    setSuggestLoading(true);
    try {
      // Some backends don't return items inline on the filter endpoint,
      // so we fetch order shells first and hydrate items via a bulk
      // salesOrderItemService.all() join — cheaper than N `get()`s.
      const [orders, allItems, products] = await Promise.all([
        salesOrderService.filter?.({ customerId: cid }) ?? salesOrderService.all(),
        salesOrderItemService.all().catch(() => [] as SalesOrderItem[]),
        productService.all().catch(() => []),
      ]);
      const productMetaById = new Map<string, { name?: string; hsnCode?: string }>();
      for (const p of products ?? []) {
        if (p.productId) productMetaById.set(p.productId, { name: p.name, hsnCode: p.hsnCode });
      }
      const itemsBySoId = new Map<string, SalesOrderItem[]>();
      for (const it of allItems ?? []) {
        if (!it.salesOrderId) continue;
        const arr = itemsBySoId.get(it.salesOrderId);
        if (arr) arr.push(it);
        else itemsBySoId.set(it.salesOrderId, [it]);
      }
      // Walk this customer's orders newest → oldest so the most recent
      // price/qty win the dedup.
      const customerOrders = (orders ?? [])
        .filter((o) => o.customerId === cid)
        .sort((a, b) => (b.orderDate ?? '').localeCompare(a.orderDate ?? ''));
      const dedup = new Map<string, SuggestedItem>();
      for (const o of customerOrders) {
        const soItems = (o.items && o.items.length > 0
          ? o.items
          : itemsBySoId.get(o.salesOrderId ?? '')) ?? [];
        for (const it of soItems) {
          const pid = it.productId;
          if (!pid) continue;
          const existing = dedup.get(pid);
          if (existing) {
            existing.orderCount += 1;
            continue;
          }
          const meta = productMetaById.get(pid);
          dedup.set(pid, {
            productId: pid,
            description: meta?.name ?? '',
            hsnCode: meta?.hsnCode ?? '',
            quantity: Number(it.quantity ?? 1) || 1,
            unitPrice: Number(it.unitPrice ?? 0),
            taxRate: Number(it.taxRate ?? 0),
            lastOrderedAt: o.orderDate,
            orderCount: 1,
          });
        }
      }
      setSuggestedItems(Array.from(dedup.values()));
    } catch {
      // Non-blocking — the admin can still add line items manually.
      setSuggestedItems([]);
    } finally {
      setSuggestLoading(false);
    }
  };

  /**
   * SSO ISSUE 8 — append a suggested product as a new line item.
   * Skips duplicates so re-ticking (or ticking after a manual add of
   * the same product) doesn't stack a second row silently.
   */
  const appendSuggested = (row: SuggestedItem) => {
    setForm((f) =>
      f.items.some((i) => i.productId === row.productId)
        ? f
        : {
            ...f,
            items: [
              ...f.items,
              {
                productId: row.productId,
                description: row.description,
                hsnCode: row.hsnCode,
                quantity: row.quantity,
                unitPrice: row.unitPrice,
                taxRate: row.taxRate,
              },
            ],
          },
    );
  };

  /**
   * SSO ISSUE 4 — when the admin picks a Sales Order, fetch it (with
   * items) and mirror every line into the invoice form. Also pulls
   * `customerId` so an admin doesn't have to pick it twice. We replace
   * the current items array outright — the previous behaviour of
   * leaving them blank was the bug; append-on-select would need a
   * confirm prompt we don't want to gate the primary path on.
   */
  const handleSalesOrderChange = async (soId: string) => {
    set('salesOrderId', soId);
    if (!soId) return;
    try {
      const so = await salesOrderService.get(soId);
      // Product names / HSN codes are on Product, not SalesOrderItem —
      // hydrate them lazily so the items grid shows real descriptions.
      const productIds = Array.from(
        new Set((so.items ?? []).map((i) => i.productId).filter(Boolean)),
      );
      const productMap = new Map<string, { name?: string; hsnCode?: string }>();
      if (productIds.length > 0) {
        await Promise.all(
          productIds.map(async (pid) => {
            try {
              const p = await productService.get(pid);
              productMap.set(pid, { name: p?.name, hsnCode: p?.hsnCode });
            } catch {
              /* leave the row's description blank on lookup failure */
            }
          }),
        );
      }
      const newItems: ItemRow[] = (so.items ?? []).map((li) => {
        const p = productMap.get(li.productId);
        return {
          productId: li.productId,
          description: p?.name ?? '',
          hsnCode: p?.hsnCode ?? '',
          quantity: Number(li.quantity ?? 0),
          unitPrice: Number(li.unitPrice ?? 0),
          taxRate: Number(li.taxRate ?? 0),
        };
      });
      setForm((f) => ({
        ...f,
        salesOrderId: soId,
        customerId: so.customerId || f.customerId,
        storeId: so.storeId || f.storeId,
        /* Carry the SO's delivery charge across. Without this the
           converted invoice under-reports by exactly the shipping fee
           (the ₹1,041 order that printed as ₹942). */
        shippingAmount: Number(so.shippingAmount ?? 0) || 0,
        items: newItems,
      }));
      if (newItems.length === 0) {
        toastRef.current.error(
          'No items on this Sales Order',
          'Add rows manually or pick a different SO.',
        );
      }
    } catch (e) {
      toastRef.current.error(
        'Sales order lookup failed',
        e instanceof Error ? e.message : 'Could not fetch items.',
      );
    }
  };

  /* Line-item helpers — array-immutable so React sees the change. */
  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx: number) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = <K extends keyof ItemRow>(idx: number, key: K, val: ItemRow[K]) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((row, i) => (i === idx ? { ...row, [key]: val } : row)),
    }));

  /**
   * Header totals reflected in the summary card + sent on the wire.
   * Recomputed on every render since it's cheap and keeps the summary
   * card in sync with any manual line edit.
   */
  const totals = useMemo(() => {
    let sub = 0;
    let tax = 0;
    let lineSum = 0;
    for (const row of form.items) {
      const { taxable, tax: lineTax, total: lineTotal } = computeLine(row);
      sub += taxable;
      tax += lineTax;
      lineSum += lineTotal;
    }
    const shipping = r2(Number(form.shippingAmount) || 0);
    /* Grand total = taxable line rows + non-taxable delivery charge.
       Mirrors InvoiceBalanceValidator:
         expected      = subTotal + cgst + sgst + igst
                         − discount + roundOff + shippingAmount
         expectedLines = totalAmount − shippingAmount               */
    return {
      sub: r2(sub),
      tax: r2(tax),
      shipping,
      lineSum: r2(lineSum),
      total: r2(lineSum + shipping),
    };
  }, [form.items, form.shippingAmount]);

  const save = async () => {
    if (!form.invoiceNumber.trim()) {
      toast.error('Fix errors', 'Invoice number is required.');
      return;
    }
    if (!form.customerId) {
      toast.error('Fix errors', 'Customer is required — pick one from the dropdown.');
      return;
    }
    /* Line-level validation: every row must have a productId + positive qty. */
    const badLine = form.items.findIndex(
      (row) => !row.productId || row.quantity <= 0,
    );
    if (badLine !== -1) {
      toast.error(
        'Fix errors',
        `Item row ${badLine + 1}: pick a product and enter a positive quantity.`,
      );
      return;
    }

    setSaving(true);
    try {
      /* Persist any freshly uploaded signature image (data-URL → hosted URL). */
      const signatureUrl = await persistImage(form.signatureImageUrl, 'misc');

      /* GST-type routing — intra-state splits CGST/SGST evenly; anything
         else (Inter-state / Exempt) drops the whole tax on IGST. Matches
         the backend Invoice.TaxAmount setter convention. */
      const gstTypeInt = GST_TYPE_BY_LABEL[form.gstType] ?? 1;
      const isIntra = gstTypeInt === 1;

      const wireItems = form.items.map((row) => {
        const { taxable, tax, total } = computeLine(row);
        return {
          productId: row.productId,
          itemName: row.description || undefined,
          hsnCode: row.hsnCode || undefined,
          quantity: row.quantity,
          unitPrice: row.unitPrice,
          discountAmount: 0,
          taxableAmount: taxable,
          cgstRate: isIntra ? row.taxRate / 2 : 0,
          sgstRate: isIntra ? row.taxRate / 2 : 0,
          igstRate: isIntra ? 0 : row.taxRate,
          cgstAmount: isIntra ? r2(tax / 2) : 0,
          sgstAmount: isIntra ? r2(tax / 2) : 0,
          igstAmount: isIntra ? 0 : tax,
          total,
        };
      });

      const cgstTotal = isIntra ? r2(totals.tax / 2) : 0;
      const sgstTotal = isIntra ? r2(totals.tax / 2) : 0;
      const igstTotal = isIntra ? 0 : totals.tax;

      const body: Invoice = {
        invoiceId: isEdit ? id : undefined,
        invoiceNumber: form.invoiceNumber,
        invoiceDate: form.invoiceDate || undefined,
        dueDate: form.dueDate || undefined,
        customerId: form.customerId || undefined,
        salesOrderId: form.salesOrderId || undefined,
        storeId: form.storeId || undefined,
        gstType: gstTypeInt,
        subTotal: totals.sub,
        discountAmount: 0,
        cgstAmount: cgstTotal,
        sgstAmount: sgstTotal,
        igstAmount: igstTotal,
        roundOff: 0,
        shippingAmount: totals.shipping,
        totalAmount: totals.total,
        paidAmount: 0,
        balanceAmount: totals.total,
        status: STATUS_BY_LABEL[form.status] ?? 1,
        notes: form.notes || undefined,
        signatureImageUrl: signatureUrl || undefined,
        // Serialised items travel on the parent payload so the backend
        // H1 map-items pipeline persists both header and lines
        // atomically. Cast: wire payload sends items without their
        // parent id (it's stamped server-side).
        items: wireItems as unknown as InvoiceItem[],
      };

      if (isEdit) {
        await invoiceService.update([body]);
        toast.success('Saved', `${form.invoiceNumber} updated`);
      } else {
        const { invoiceId: _o, ...create } = body;
        await invoiceService.create([create as unknown as InvoiceCreate]);
        toast.success('Saved', `${form.invoiceNumber} added`);
      }
      navigate('/panel/invoices');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-m-6 p-[3px]">
      {err && (
        <Alert variant="danger" className="mb-3">
          {err}
        </Alert>
      )}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/invoices')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit invoice' : 'New invoice'}
          </h2>
        </div>
        {loading ? (
          <div className="text-sm text-content-muted">Loading…</div>
        ) : (
          <>
            {/* ────── Header fields ────── */}
            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">
                  Invoice number <span className="text-danger">*</span>
                </span>
                <input
                  value={form.invoiceNumber}
                  onChange={(e) => set('invoiceNumber', e.target.value)}
                  placeholder="INV-1001"
                  className={inputCls}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Invoice date</span>
                <input
                  type="date"
                  value={form.invoiceDate}
                  onChange={(e) => set('invoiceDate', e.target.value)}
                  className={inputCls}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Due date</span>
                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => set('dueDate', e.target.value)}
                  className={inputCls}
                />
              </label>
              <SearchableSelect
                label="Customer *"
                value={form.customerId}
                onChange={handleCustomerChange}
                loadOptions={loadCustomerOptsWithHistory}
                placeholder="Pick a customer"
                searchPlaceholder="Search customers…"
                emptyLabel="No customers with prior orders yet"
              />
              {/* SSO ISSUE 4 — picking an SO now populates line items.
                  SSO ISSUE 8 — the option label includes product names
                  so admins can identify the order at a glance. */}
              <SearchableSelect
                label="Sales order (auto-hydrates items)"
                value={form.salesOrderId}
                onChange={handleSalesOrderChange}
                loadOptions={loadSalesOrderOptsWithItems}
                placeholder="Link a sales order to prefill items"
                searchPlaceholder="Search sales orders…"
              />
              <SearchableSelect
                label="Store"
                value={form.storeId}
                onChange={(v) => set('storeId', v)}
                loadOptions={loadStoreOptions}
                placeholder="Select a store"
                searchPlaceholder="Search stores…"
              />
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">GST type</span>
                <select
                  value={form.gstType}
                  onChange={(e) => set('gstType', e.target.value)}
                  className={selectCls}
                >
                  {Object.keys(GST_TYPE_BY_LABEL).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Status</span>
                <select
                  value={form.status}
                  onChange={(e) => set('status', e.target.value)}
                  className={selectCls}
                >
                  {Object.keys(STATUS_BY_LABEL).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-xs font-medium text-content-secondary">Notes</span>
                <textarea
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={2}
                  className={`${inputCls} h-auto py-2`}
                  placeholder="Optional notes for the printed invoice…"
                />
              </label>
            </div>

            {/* ────── SSO ISSUE 8 — suggested items from prior orders ────── */}
            {(suggestLoading || suggestedItems.length > 0) && (
              <div className="mt-6 rounded-md border border-border bg-surface-card-hover p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-content-primary">
                    Suggested items from previous orders
                  </h3>
                  <span className="text-[11px] text-content-muted">
                    {suggestLoading
                      ? 'Loading…'
                      : `${suggestedItems.length} product${suggestedItems.length === 1 ? '' : 's'}`}
                  </span>
                </div>
                {!suggestLoading && suggestedItems.length > 0 && (
                  <div className="grid gap-1 md:grid-cols-2">
                    {suggestedItems.map((row) => {
                      const alreadyOn = form.items.some((i) => i.productId === row.productId);
                      return (
                        <label
                          key={row.productId}
                          className="flex cursor-pointer items-center gap-2 rounded border border-border bg-surface-card px-2 py-1.5 text-sm hover:bg-surface-card-hover"
                        >
                          <input
                            type="checkbox"
                            checked={alreadyOn}
                            disabled={alreadyOn}
                            onChange={(e) => {
                              if (e.target.checked) appendSuggested(row);
                            }}
                          />
                          <span className="flex-1 truncate">
                            {row.description || row.productId}
                          </span>
                          <span className="text-[11px] text-content-muted">
                            ₹{row.unitPrice.toFixed(2)}
                            {row.orderCount > 1 ? ` · ${row.orderCount}×` : ''}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ────── Items grid ────── */}
            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-content-primary">Line items</h3>
                <Button variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-3.5 w-3.5" /> Add row
                </Button>
              </div>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-surface-card-hover text-content-secondary">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium">Product</th>
                      <th className="px-2 py-2 text-left font-medium">Description</th>
                      <th className="px-2 py-2 text-right font-medium">Qty</th>
                      <th className="px-2 py-2 text-right font-medium">Unit price (₹)</th>
                      <th className="px-2 py-2 text-right font-medium">Tax %</th>
                      <th className="px-2 py-2 text-right font-medium">Amount (₹)</th>
                      <th className="px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-2 py-6 text-center text-content-muted"
                        >
                          No items yet — link a Sales Order above, or click "Add row".
                        </td>
                      </tr>
                    ) : (
                      form.items.map((row, idx) => {
                        const line = computeLine(row);
                        return (
                          <tr key={idx} className="border-t border-border">
                            <td className="min-w-[220px] px-2 py-2">
                              <SearchableSelect
                                value={row.productId}
                                onChange={(v) => updateItem(idx, 'productId', v)}
                                loadOptions={loadProductOpts}
                                /* Flip the panel upward when the row is
                                   near the viewport bottom so the picker
                                   doesn't bury the signature / save bar
                                   below the items table. */
                                placement="auto"
                                placeholder="Pick a product"
                                searchPlaceholder="Search products…"
                              />
                            </td>
                            <td className="min-w-[180px] px-2 py-2">
                              <input
                                value={row.description}
                                onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                placeholder="Item description"
                                className={inputCls}
                              />
                            </td>
                            <td className="w-24 px-2 py-2">
                              <NumberField
                                min={0}
                                step="1"
                                value={row.quantity}
                                onValueChange={(n) => updateItem(idx, 'quantity', n)}
                                className={`${inputCls} text-right`}
                              />
                            </td>
                            <td className="w-32 px-2 py-2">
                              <NumberField
                                min={0}
                                step="0.01"
                                value={row.unitPrice}
                                onValueChange={(n) => updateItem(idx, 'unitPrice', n)}
                                className={`${inputCls} text-right`}
                              />
                            </td>
                            <td className="w-20 px-2 py-2">
                              <NumberField
                                min={0}
                                step="0.01"
                                value={row.taxRate}
                                onValueChange={(n) => updateItem(idx, 'taxRate', n)}
                                className={`${inputCls} text-right`}
                              />
                            </td>
                            <td className="w-28 px-2 py-2 text-right font-medium">
                              ₹{line.total.toFixed(2)}
                            </td>
                            <td className="w-10 px-1 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => removeItem(idx)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
                                title="Remove row"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {/* Live totals — mirror the backend balance validator so the
                  admin sees what will actually be persisted. */}
              <div className="mt-3 grid gap-1 text-sm md:ml-auto md:max-w-xs">
                <div className="flex justify-between">
                  <span className="text-content-muted">Sub total</span>
                  <span className="font-medium">₹{totals.sub.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-content-muted">Tax</span>
                  <span className="font-medium">₹{totals.tax.toFixed(2)}</span>
                </div>
                {/* F2 — non-taxable delivery / handling charge. Editable so a
                    manually-raised invoice can carry the same fee the sales
                    order charged; prefilled on SO conversion. */}
                <div className="flex items-center justify-between gap-2">
                  <label htmlFor="inv-shipping" className="text-content-muted">
                    Shipping / handling
                  </label>
                  <div className="flex items-center gap-1">
                    <span className="text-content-muted">₹</span>
                    <input
                      id="inv-shipping"
                      type="number"
                      min={0}
                      step="0.01"
                      value={form.shippingAmount}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          shippingAmount: Number(e.target.value) || 0,
                        }))
                      }
                      className="h-8 w-24 rounded-md border border-border bg-surface-card px-2 text-right text-sm text-content-primary outline-none focus:border-primary"
                    />
                  </div>
                </div>
                <div className="flex justify-between border-t border-border pt-1 text-base font-bold">
                  <span>Total</span>
                  <span>₹{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* ────── SSO ISSUE 4 — authorized signature ────── */}
            <div className="mt-6 border-t border-border pt-4">
              <ImagePicker
                label="Authorized signature"
                value={form.signatureImageUrl}
                onChange={(v) => set('signatureImageUrl', v)}
                hint="Upload the signatory's scanned signature (PNG/JPG, ≤ 200 KB). Rendered on the printed invoice."
                previewSize={100}
              />
            </div>
          </>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate('/panel/invoices')}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading || !canSave}
            title={!canSave ? `You don't have permission to ${isEdit ? 'update' : 'create'} invoices` : undefined}
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
